import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Input, Alert } from '@components/ui';
import { cn } from '@lib/utils/cn';
import { useAuth } from '../hooks/useAuth';
import { TEST_IDS } from '@lib/utils/testIds';

/**
 * Map backend error codes to user-friendly messages and alert variants.
 */
function getLoginErrorInfo(error: any): { message: string; variant: 'danger' | 'warning' } {
  const code = error?.response?.data?.code;
  const status = error?.response?.status;

  // Service unavailable (Aludra down, timeout, network issues)
  if (status === 503 || code === 'SERVICE_UNAVAILABLE' || code === 'SERVICE_TIMEOUT' || code === 'SERVICE_ERROR') {
    return {
      message: 'El servicio no está disponible en este momento. Por favor, intenta nuevamente en unos minutos.',
      variant: 'warning',
    };
  }

  // Server error (unexpected failure after authentication succeeded)
  // Must come before the default 401 check so 500s aren't shown as "invalid credentials"
  if (status === 500) {
    return {
      message: 'Ocurrió un error inesperado en el servidor. Por favor, intenta nuevamente en unos minutos.',
      variant: 'warning',
    };
  }

  // Account locked
  if (status === 403 || code === 'ACCOUNT_LOCKED') {
    return {
      message: 'Tu cuenta ha sido bloqueada. Contacta al administrador.',
      variant: 'danger',
    };
  }

  // Auth rate limited
  if (status === 429 || code === 'AUTH_RATE_LIMITED') {
    return {
      message: 'Demasiados intentos de inicio de sesión. Por favor, intenta nuevamente en 15 minutos.',
      variant: 'warning',
    };
  }

  // Two-factor required
  if (code === 'TWO_FACTOR_REQUIRED') {
    return {
      message: 'Se requiere autenticación de dos factores.',
      variant: 'warning',
    };
  }

  // Network error (no response from our own backend)
  if (!error?.response) {
    return {
      message: 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente.',
      variant: 'warning',
    };
  }

  // Default: invalid credentials (only for actual 401)
  return {
    message: 'Credenciales inválidas. Por favor, verifica tu correo y contraseña.',
    variant: 'danger',
  };
}

const loginSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Development credentials from environment variables (NOT hardcoded)
// Set VITE_DEV_EMAIL and VITE_DEV_PASSWORD in your local .env.development
const getDevCredentials = () => {
  const email = import.meta.env.VITE_DEV_EMAIL || '';
  const password = import.meta.env.VITE_DEV_PASSWORD || '';
  return { email, password };
};

// Check if dev credentials are configured
const hasDevCredentials = () => {
  return !!(import.meta.env.VITE_DEV_EMAIL && import.meta.env.VITE_DEV_PASSWORD);
};

function handleAzureLogin() {
  const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';
  // Navegación completa de página (no fetch): el backend redirige a Microsoft.
  window.location.href = `${baseURL}/auth/azure/redirect`;
}

/** Estilos del diseño (Claude Design · Login.dc.html), compartidos por los campos. */
const FIELD_LABEL = 'text-xs font-semibold text-ink-850';
const FIELD_INPUT =
  'block w-full rounded-[10px] border border-[#ececec] bg-white px-4 py-[15px] text-[15px] font-medium leading-6 text-ink-850 ' +
  'placeholder:font-normal placeholder:text-secondary-400 ' +
  'hover:border-secondary-300 ' +
  'focus:border-secondary-400 focus:ring-4 focus:ring-secondary-400/[.14] ' +
  'transition-colors';

/**
 * El borde rojo de error hay que re-aplicarlo acá: Input lo pone antes que
 * `className` dentro de cn(), así que tailwind-merge deja ganar el borde
 * neutro del diseño y el campo inválido quedaría sin señal visual.
 */
const FIELD_INPUT_ERROR =
  'border-danger-500 hover:border-danger-500 focus:border-danger-500 focus:ring-danger-500/20';

interface LoginFormProps {
  /** true si el tenant actual tiene login federado con Azure AD habilitado. */
  azureAdEnabled?: boolean;
  /**
   * Presente cuando el usuario ya verificó su email con Microsoft (ver
   * AzureCallbackPage.tsx / LoginPage.tsx) y solo falta escribir su password
   * de Aludra. NO es un login sin contraseña: Aludra la exige siempre.
   */
  azureVerification?: { email: string; azureRef: string } | null;
}

export function LoginForm({ azureAdEnabled = false, azureVerification = null }: LoginFormProps) {
  const { login, isLoggingIn, loginError, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const isDevelopment = import.meta.env.VITE_APP_ENV === 'development';
  const autoLoginEnabled = import.meta.env.VITE_AUTO_LOGIN === 'true';
  const devCredentialsConfigured = hasDevCredentials();
  const devCredentials = getDevCredentials();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: azureVerification?.email
        ?? (isDevelopment && autoLoginEnabled && devCredentialsConfigured ? devCredentials.email : ''),
      password: isDevelopment && autoLoginEnabled && devCredentialsConfigured ? devCredentials.password : '',
      rememberMe: false,
    },
  });

  // Auto-login en desarrollo (solo si están configuradas las credenciales en .env)
  // Nunca cuando viene de verificar con Microsoft: pisaría el email verificado
  // y saltaría la captura del password real.
  useEffect(() => {
    if (isDevelopment && autoLoginEnabled && devCredentialsConfigured && !azureVerification && !isAuthenticated && !isLoggingIn && !loginError) {
      login({
        email: devCredentials.email,
        password: devCredentials.password,
        rememberMe: true,
      });
    }
  }, [isDevelopment, autoLoginEnabled, devCredentialsConfigured, azureVerification, isAuthenticated, isLoggingIn, loginError, login, devCredentials]);

  const onSubmit = (data: LoginFormData) => {
    if (azureVerification) {
      // El email visible viene readonly con el valor verificado; se manda
      // explícito para no depender de que el input readonly haya llegado
      // intacto, y azureRef para que el backend recupere el tokenAzure real
      // (que nunca pasa por el navegador) y lo sume al password.
      login({ email: azureVerification.email, password: data.password, azureRef: azureVerification.azureRef });
      return;
    }
    login(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 short:space-y-4" data-testid={TEST_IDS.AUTH.LOGIN.FORM}>
      {isDevelopment && autoLoginEnabled && devCredentialsConfigured && (
        <Alert variant="info" title="Modo Desarrollo">
          Auto-login activado con credenciales de entorno
        </Alert>
      )}

      {loginError && (() => {
        const errorInfo = getLoginErrorInfo(loginError);
        return (
          <Alert variant={errorInfo.variant} title="Error al iniciar sesión" data-testid={TEST_IDS.AUTH.LOGIN.ERROR_ALERT}>
            {errorInfo.message}
          </Alert>
        );
      })()}

      {azureVerification && (
        <Alert variant="info" title="Verificado con Microsoft">
          <span className="font-medium">{azureVerification.email}</span> — ingresa tu contraseña de
          Aludra para continuar.
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <label htmlFor="email" className={FIELD_LABEL}>
          Correo electrónico
        </label>
        <Input
          id="email"
          type="email"
          placeholder="nombre@empresa.com"
          error={errors.email?.message}
          data-testid={TEST_IDS.AUTH.LOGIN.EMAIL_INPUT}
          readOnly={!!azureVerification}
          tabIndex={azureVerification ? -1 : undefined}
          className={cn(
            FIELD_INPUT,
            azureVerification && 'cursor-not-allowed bg-secondary-100 text-secondary-500',
            errors.email && FIELD_INPUT_ERROR
          )}
          {...register('email')}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <label htmlFor="password" className={FIELD_LABEL}>
            Contraseña
          </label>
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-secondary-500 transition-colors hover:text-ink-850"
            data-testid={TEST_IDS.AUTH.LOGIN.FORGOT_PASSWORD_LINK}
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••••••"
            error={errors.password?.message}
            data-testid={TEST_IDS.AUTH.LOGIN.PASSWORD_INPUT}
            className={cn(FIELD_INPUT, 'pr-12 tracking-[0.1em]', errors.password && FIELD_INPUT_ERROR)}
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            aria-pressed={showPassword}
            className="absolute right-2 top-0 flex h-[58px] items-center px-2 text-secondary-400 transition-colors hover:text-ink-850"
          >
            {showPassword ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <label htmlFor="rememberMe" className="inline-flex cursor-pointer select-none items-center gap-2.5">
        <input
          id="rememberMe"
          type="checkbox"
          className="h-4 w-4 rounded border-[1.5px] border-secondary-300 text-ink-850 focus:ring-secondary-400"
          data-testid={TEST_IDS.AUTH.LOGIN.REMEMBER_CHECKBOX}
          {...register('rememberMe')}
        />
        <span className="text-[14px] font-medium text-ink-850">Recordarme</span>
      </label>

      <button
        type="submit"
        disabled={isLoggingIn}
        className="cta-sheen mt-2 flex w-full items-center justify-between gap-3 rounded-[10px] bg-ink-850 px-[22px] py-[17px] text-[15px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-70"
        data-testid={TEST_IDS.AUTH.LOGIN.SUBMIT_BUTTON}
      >
        <span>{isLoggingIn ? 'Iniciando sesión…' : 'Iniciar sesión'}</span>
        {isLoggingIn ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : (
          <svg className="cta-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        )}
      </button>

      {azureAdEnabled && !azureVerification && (
        <>
          <div className="relative my-6 short:my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-secondary-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-3 text-secondary-400">o</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleAzureLogin}
            className="flex w-full items-center justify-center gap-2.5 rounded-[10px] border border-[#ececec] bg-white px-[22px] py-[15px] text-[15px] font-semibold text-ink-850 transition-colors hover:border-secondary-300 hover:bg-secondary-50"
            data-testid="azure-ad-login-button"
          >
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" className="flex-shrink-0">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Iniciar sesión con Microsoft
          </button>
        </>
      )}

      <div className="mt-8 flex items-center justify-between border-t border-secondary-100 pt-5 text-sm text-secondary-400 short:mt-4 short:pt-3">
        <span>
          Impulsado por <span className="font-semibold text-ink-850">HyperNova Labs</span>
        </span>
        <span
          className="font-mono tracking-[0.06em]"
          title={`Build: ${import.meta.env.VITE_APP_VERSION || 'dev'}`}
        >
          v{__APP_VERSION__}
        </span>
      </div>
    </form>
  );
}
